/**
 * What the app is allowed to say about a number.
 *
 * ONE PREDICATE, IMPORTED BY THE CHART AND BY ITS CAPTION. A chart that draws a
 * contrast and a caption that hedges it are two statements of the same rule,
 * and a restatement is never checked against the rule. Both come from here.
 *
 * WHY IT EXISTS. Most of what this data looks like it could say, it cannot. Day
 * marks are the worked example: twenty-two marked days, eight kinds, and the
 * day-end mood on each against an overall mean of −0.21 —
 *
 *   alcohol +0.13 (8 days)   sex −0.11 (9)    saw people −0.23 (13)
 *   unwell  −0.40 (5)        exercise −0.70 (10)
 *   short night −3.00 (1)    argument −3.00 (1)
 *
 * — every one of which is inside the retest error of a single momentary mood
 * item, about a tenth of the scale (Dejonckheere et al. 2022), or rests on
 * fewer than five days. A ranked bar chart of that table prints "EXERCISE —
 * YOUR WORST DAYS" in large type, which is noise, and is also advice that would
 * harm somebody. The app he admires can rank its categories because a tenfold
 * spread sits above its noise; this one cannot, and the difference has to live
 * somewhere a chart can import.
 *
 * THE REFUSAL IS A RESULT, NOT AN ERROR. `notEnough` carries what it would
 * take, because "ask again at fifteen days" is a more useful thing to draw than
 * a guess, and far more useful than an empty box.
 */
/** A tenth of the −5..+5 scale: the retest error on one momentary rating. */
export const MOOD_NOISE = 1;
/** Below this, a mean is arithmetic about a handful of days and nothing more. */
export const MIN_DAYS_FOR_A_CONTRAST = 5;
/**
 * May the app state this contrast?
 *
 * Deliberately conservative in both directions: a contrast has to clear the
 * sample floor AND exceed the noise, and neither test is a judgement call made
 * at the call site.
 */
export function evidenceFor({ n, against, effect, minN = MIN_DAYS_FOR_A_CONTRAST, noise = MOOD_NOISE, }) {
    if (n < minN)
        return { kind: 'tooFew', n, needed: minN };
    /* BOTH SIDES, and the order matters: this is checked before the noise bar,
       so a contrast with too little to compare against is refused on the honest
       ground rather than on the size of a difference that cannot be measured. */
    if (against !== undefined && against < minN) {
        return { kind: 'tooFewAgainst', n, against, needed: minN };
    }
    if (Math.abs(effect) < noise)
        return { kind: 'insideNoise', n, effect, noise };
    return { kind: 'enough', n };
}
/**
 * THE BARS, IN WORDS, ONE PER WAY OF FAILING.
 *
 * Keyed by the Evidence kind it corresponds to, and typed as a Record over
 * every kind except `enough` — so adding a fourth way to refuse something does
 * not compile until the words for it exist. That is the only mechanism here
 * that actually holds: the caption on the day-marks plate SPELLS THESE OUT,
 * and when `tooFewAgainst` was added the caption went on saying "not one of
 * them yet clears both bars -- at least 5 days carrying it, and a difference
 * bigger than 1 point". On Nick's real fourteen-day window that caption then
 * sat directly above "Saw people, 5 days" -- which clears both of the two bars
 * it names, and was refused for the third one it does not.
 *
 * CLAUDE.md: "Copy that describes a gate is a second implementation of that
 * gate." The predicate moved and its restatement did not, inside the very
 * commit that moved it. A Record the compiler checks is a restatement that
 * cannot drift.
 */
export const CONTRAST_BARS = {
    /* No internal commas: these are drawn as a list, and a comma inside an item
       read as the separator between items when they were joined inline. */
    tooFew: `at least ${MIN_DAYS_FOR_A_CONTRAST} days carrying it`,
    tooFewAgainst: `at least ${MIN_DAYS_FOR_A_CONTRAST} days without it to compare against`,
    insideNoise: `a difference bigger than ${MOOD_NOISE} point — the wobble between two check-ins taken an hour apart`,
};
/** "two"… "four" — so a caption listing the bars can count them correctly. */
export const BAR_COUNT_WORD = ['no', 'one', 'two', 'three', 'four', 'five'][Object.keys(CONTRAST_BARS).length];
/** Whether a chart may draw this at all. */
export function mayState(e) {
    return e.kind === 'enough';
}
/**
 * The sentence for a refusal, in the app's own voice and never an apology.
 *
 * It says what is true and what would change it. "Not enough to read anything
 * into" is a finding; "no data" is a shrug, and an empty box is worse than
 * either because a reader fills it in themselves.
 */
export function evidenceSentence(e, unit = 'day') {
    const plural = (k) => `${k} ${unit}${k === 1 ? '' : 's'}`;
    switch (e.kind) {
        case 'tooFew':
            return e.n === 0
                ? `Nothing recorded for this yet. It needs ${plural(e.needed)} before it says anything.`
                : `Only ${plural(e.n)}. Too few to read anything into — ask again at ${plural(e.needed)}.`;
        case 'tooFewAgainst':
            return `${plural(e.n)}, but only ${plural(e.against)} without it in this window — too little to compare against. A shorter window makes this worse, not better.`;
        case 'insideNoise':
            return `${plural(e.n)}, and the difference is smaller than the measurement error on a single rating. Not something to act on.`;
        case 'enough':
            return `${plural(e.n)}.`;
    }
}
/**
 * How far each count sits from an even spread, in standard deviations.
 *
 * The reference is the range he has ACTUALLY USED, not the nominal scale, and
 * that choice changes the answer: spread over all eleven points the three he
 * has never chosen drag the expectation down and every unused end reads as
 * −2.5 SD, which is a fact about the scale rather than about him. Over the
 * eight he uses, only one point stands out.
 *
 * Returned rather than asserted so a caption can quote it and be checked.
 */
export function spreadAgainstFlat(counts) {
    const used = Object.entries(counts).filter(([, c]) => c > 0);
    const n = used.reduce((a, [, c]) => a + c, 0);
    const points = used.length;
    const expected = points > 0 ? n / points : 0;
    const sd = Math.sqrt(expected);
    const z = {};
    let furthest = null;
    for (const [k, c] of used) {
        const value = sd > 0 ? (c - expected) / sd : 0;
        z[Number(k)] = value;
        if (!furthest || Math.abs(value) > Math.abs(furthest.z))
            furthest = { point: Number(k), z: value };
    }
    return { n, points, expected, sd, z, furthest };
}
//# sourceMappingURL=evidence.js.map